import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  InputSignal,
  OutputEmitterRef,
  Signal,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterLink } from '@angular/router';
import { Observable, filter, map, startWith } from 'rxjs';

import {
  CoreInsightsEvent,
  HeaderContentService,
  HeaderElementPlacement,
  HeaderElementsMap,
  InsightsContextAware,
  ThemeAssetsPathPipe,
} from '@core/shared/util';

@Component({
  selector: 'mp-app-header',
  templateUrl: './app-header.component.html',
  styleUrl: './app-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgTemplateOutlet, RouterLink, MatIconButton, MatIcon, ThemeAssetsPathPipe],
})
export class AppHeaderComponent extends InsightsContextAware {
  private readonly headerContentService: HeaderContentService = inject(HeaderContentService);
  private readonly router: Router = inject(Router);

  private readonly _activatedRouteTitle: Signal<string | undefined> = toSignal(
    this.buildInternalActivatedRouteTitleObservable(),
  );

  readonly showAppLogo: InputSignal<boolean> = input.required<boolean>();
  readonly appTitle: InputSignal<string> = input.required<string>();

  readonly sidenavToggle: OutputEmitterRef<void> = output<void>();

  readonly headerElementsMap: Signal<HeaderElementsMap> = this.headerContentService.headerElementsMap;

  readonly activeRouteTitle: Signal<string> = computed(() => {
    return this._activatedRouteTitle() || this.appTitle();
  });

  protected readonly headerElementPlacements: HeaderElementPlacement[] = ['left', 'center', 'right'];

  toggleSidenav(): void {
    this.sidenavToggle.emit();
  }

  trackHomePageNavigation(): void {
    if (this.isActive()) {
      this.trackInsightsEvent(CoreInsightsEvent.HOME_PAGE_NAVIGATION, {}, { includePageName: false });
    }
  }

  private isActive(): boolean {
    return this.router.isActive('', {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }

  private buildInternalActivatedRouteTitleObservable(): Observable<string | undefined> {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      startWith({}),
      map(() => this.getActiveModuleRoute(this.router.routerState.snapshot.root).title),
    );
  }

  private getActiveModuleRoute(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    // Active module route is always the third child from application root due to the routing structure
    return snapshot.firstChild?.firstChild?.firstChild || snapshot;
  }
}
